import { api } from '@/lib/api'
import type {
  AddSourceInput, AllocateInput, Bank, CashPayment, CashScheduleItem, CashScheduleTemplate, CashSplit, Financing,
  RefundInput, RegisterPaymentInput, SaveCashScheduleTemplateInput, UpdateSourceInput,
} from './types'

// --- Szablony harmonogramu (admin: per konfiguracja lub per produkt) ---

/** scope: 'configurations' (fallback) | 'products' (nadpisanie per typ produktu). */
export type ScheduleTemplateScope = 'configurations' | 'products'

export async function getScheduleTemplate(scope: ScheduleTemplateScope, code: string): Promise<CashScheduleTemplate> {
  const r = await api.get<CashScheduleTemplate>(`/api/${scope}/${code}/cash-schedule-template`)
  return r.data
}

export async function saveScheduleTemplate(
  scope: ScheduleTemplateScope, code: string, input: SaveCashScheduleTemplateInput,
): Promise<CashScheduleTemplate> {
  const r = await api.put<CashScheduleTemplate>(`/api/${scope}/${code}/cash-schedule-template`, input)
  return r.data
}

export async function deleteScheduleTemplate(scope: ScheduleTemplateScope, code: string): Promise<void> {
  await api.delete(`/api/${scope}/${code}/cash-schedule-template`)
}

// --- Harmonogram + wpłaty na poziomie umowy (Faza B) ---

export async function listContractSchedule(contractId: number): Promise<CashScheduleItem[]> {
  const r = await api.get<CashScheduleItem[]>(`/api/contracts/${contractId}/financing/cash-schedule`)
  return r.data
}

export async function getCashSplit(contractId: number, cashAmount: number): Promise<CashSplit> {
  const r = await api.get<CashSplit>(`/api/contracts/${contractId}/financing/cash-schedule/split`, {
    params: { cashAmount },
  })
  return r.data
}

export async function generateContractSchedule(contractId: number, cashAmount: number): Promise<CashScheduleItem[]> {
  const r = await api.post<CashScheduleItem[]>(`/api/contracts/${contractId}/financing/cash-schedule/generate`, { cashAmount })
  return r.data
}

export async function listPayments(contractId: number): Promise<CashPayment[]> {
  const r = await api.get<CashPayment[]>(`/api/contracts/${contractId}/financing/payments`)
  return r.data
}

export async function registerPayment(contractId: number, input: RegisterPaymentInput): Promise<CashPayment> {
  const r = await api.post<CashPayment>(`/api/contracts/${contractId}/financing/payments`, input)
  return r.data
}

export async function allocatePayment(contractId: number, paymentId: number, input: AllocateInput): Promise<CashPayment> {
  const r = await api.post<CashPayment>(`/api/contracts/${contractId}/financing/payments/${paymentId}/allocations`, input)
  return r.data
}

export async function deleteAllocation(contractId: number, paymentId: number, allocationId: number): Promise<CashPayment> {
  const r = await api.delete<CashPayment>(`/api/contracts/${contractId}/financing/payments/${paymentId}/allocations/${allocationId}`)
  return r.data
}

export async function deletePayment(contractId: number, paymentId: number): Promise<void> {
  await api.delete(`/api/contracts/${contractId}/financing/payments/${paymentId}`)
}

export async function registerRefund(contractId: number, input: RefundInput): Promise<CashPayment> {
  const r = await api.post<CashPayment>(`/api/contracts/${contractId}/financing/payments/refunds`, input)
  return r.data
}

const scheduleBase = (contractId: number, elementId: number) =>
  `/api/contracts/${contractId}/financing/elements/${elementId}/cash-schedule`

export async function listCashSchedule(contractId: number, elementId: number): Promise<CashScheduleItem[]> {
  const r = await api.get<CashScheduleItem[]>(scheduleBase(contractId, elementId))
  return r.data
}

export async function generateCashSchedule(contractId: number, elementId: number, cashAmount: number): Promise<CashScheduleItem[]> {
  const r = await api.post<CashScheduleItem[]>(`${scheduleBase(contractId, elementId)}/generate`, { cashAmount })
  return r.data
}

export async function confirmTranche(
  contractId: number, elementId: number, itemId: number, payload: { paidAt: string; paidAmount: number },
): Promise<CashScheduleItem> {
  const r = await api.post<CashScheduleItem>(`${scheduleBase(contractId, elementId)}/items/${itemId}/confirm`, payload)
  return r.data
}

const base = (contractId: number) => `/api/contracts/${contractId}/financing`

export async function getFinancing(contractId: number): Promise<Financing> {
  const r = await api.get<Financing>(base(contractId))
  return r.data
}

export async function addSource(contractId: number, input: AddSourceInput): Promise<Financing> {
  const r = await api.post<Financing>(`${base(contractId)}/sources`, input)
  return r.data
}

export async function updateSource(contractId: number, sourceId: number, input: UpdateSourceInput): Promise<Financing> {
  const r = await api.put<Financing>(`${base(contractId)}/sources/${sourceId}`, input)
  return r.data
}

export async function deleteSource(contractId: number, sourceId: number): Promise<Financing> {
  const r = await api.delete<Financing>(`${base(contractId)}/sources/${sourceId}`)
  return r.data
}

export async function listBanks(activeOnly = true): Promise<Bank[]> {
  const r = await api.get<Bank[]>('/api/banks', { params: { activeOnly } })
  return r.data
}

export async function createBank(payload: { code: string; labelPl: string; sortOrder: number; isActive: boolean }): Promise<Bank> {
  const r = await api.post<Bank>('/api/banks', payload)
  return r.data
}

export async function updateBank(code: string, payload: { labelPl: string; sortOrder: number; isActive: boolean }): Promise<Bank> {
  const r = await api.put<Bank>(`/api/banks/${code}`, { code, ...payload })
  return r.data
}

export async function deleteBank(code: string): Promise<void> {
  await api.delete(`/api/banks/${code}`)
}
